import { Eye } from 'lucide-react';
import styled from 'styled-components';

import { IconButton } from '../../../components/atoms/icon-button/icon-button';
import { SaqueListItem } from '../../../types/saque';
import { formatDateTime, formatMoney } from './format';
import { SaqueStatusBadge } from './saque-status-badge';

interface Props {
  items: SaqueListItem[];
  onOpenDetail: (id: string) => void;
}

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};

  thead th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.textMuted};
    background: ${({ theme }) => theme.colors.bgMuted};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    font-weight: 600;
  }

  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  th:last-child,
  td:last-child {
    text-align: right;
  }
`;

const ClienteCell = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  strong {
    font-weight: 600;
  }

  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12.5px;
  }
`;

const ValorCell = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;

  strong {
    font-weight: 600;
  }

  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12.5px;
    font-weight: 500;
  }
`;

export const SaquesTable = ({ items, onOpenDetail }: Props) => (
  <Table>
    <caption className="sr-only">Lista de saques</caption>
    <thead>
      <tr>
        <th scope="col">Cliente</th>
        <th scope="col">Valor</th>
        <th scope="col">Status</th>
        <th scope="col">Solicitado em</th>
        <th scope="col">Ações</th>
      </tr>
    </thead>
    <tbody>
      {items.map((saque) => (
        <tr key={saque.id} data-testid={`saque-row-${saque.id}`}>
          <td>
            <ClienteCell>
              <strong>
                {saque.cliente.nome}{' '}
                {saque.cliente.sobrenome ?? ''}
              </strong>
              <small>{saque.cliente.email}</small>
            </ClienteCell>
          </td>
          <td>
            <ValorCell>
              <strong>{formatMoney(saque.valor, saque.moeda)}</strong>
              <small>
                Líquido {formatMoney(saque.valorLiquido, saque.moedaDestino)}
              </small>
            </ValorCell>
          </td>
          <td>
            <SaqueStatusBadge
              status={saque.status}
              description={saque.statusDescricao}
            />
          </td>
          <td>{formatDateTime(saque.createdAt)}</td>
          <td>
            <IconButton
              label={`Ver detalhes do saque de ${saque.cliente.nome}`}
              onClick={() => onOpenDetail(saque.id)}
            >
              <Eye size={16} />
            </IconButton>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);
